<ng-template #filtersToggle>
  @let filtersLabel = library.hasFilters() ? 'Filters (applied)' : 'Filters';
  <button
    #filtersButton
    mibButton
    type="button"
    theme="secondary"
    activeTone="primary"
    size="sm"
    class="shrink-0"
    [class.ml-auto]="notExpanded()"
    [attr.aria-label]="filtersLabel"
    [mode]="notExpanded() ? 'filled' : 'ghost'"
    [mibTooltip]="filtersLabel"
    [active]="filtersOpen() || library.hasFilters()"
    [attr.aria-expanded]="compact() ? null : filtersOpen()"
    [attr.aria-controls]="compact() ? null : 'library-filters'"
    [attr.aria-haspopup]="compact() ? 'dialog' : null"
    (click)="toggleFilters()">
    <svg mib-icon name="funnel"></svg>
  </button>
</ng-template>

<!-- The heading and the row above the cases, shared by both layouts. -->
<ng-template #toolbar let-count>
  <div class="flex items-center gap-3">
    <h1
      class="text-mib-branding-900 min-w-0 truncate text-2xl font-semibold not-expanded:leading-7.5 compact:ml-1">
      My case library
    </h1>
    <span class="shrink-0 text-sm text-gray-500">{{ countLabel(count) }}</span>
    @if (notExpanded()) {
      <ng-container [ngTemplateOutlet]="filtersToggle"></ng-container>
    }
  </div>

  <!--
   Controlled: the row reports the selection the page should apply and never changes it
   itself, so a chip and the cases under it cannot come to disagree.
  -->
  @if (!notExpanded()) {
    <div class="flex items-center gap-3">
      <ng-container [ngTemplateOutlet]="filtersToggle"></ng-container>

      <!-- Narrower than the row of chips needs, the same choices fold into a menu. -->
      <button
        mibButton
        type="button"
        theme="secondary"
        shape="round"
        class="hidden shrink-0 whitespace-nowrap @max-[980px]:inline-flex"
        [active]="library.categoryRow().length > 0"
        [mibMenuTrigger]="rowMenu">
        {{ rowLabel() }}
        <svg mib-icon name="chevron-down" class="size-4"></svg>
      </button>
      <mib-menu #rowMenu [menuItems]="rowItems()" (itemSelected)="toggleRow($event)">
        <ng-template mibMenuItemContent let-item let-selected="selected">
          <span class="flex size-5 shrink-0 items-center justify-center" aria-hidden="true">
            @if (item.icon) {
              <svg
                mib-icon
                [name]="item.icon"
                class="size-4"
                [class]="selected ? 'text-mib-favourite [&_path]:fill-current' : ''"></svg>
            } @else {
              <span [class]="rowDot(item)"></span>
            }
          </span>
          <span class="truncate">{{ item.label }}</span>
        </ng-template>
      </mib-menu>

      @if (!filtersOpen()) {
        <mib-filter-chips
          class="min-w-0 @max-[980px]:hidden"
          animate.leave="chips-leaving"
          [class]="chipsMotionClasses()"
          aria-label="Filter the library"
          [chips]="categoryChips"
          [value]="library.categoryRow()"
          (valueChange)="library.setCategoryRow($event)"></mib-filter-chips>
      }

      <!-- After the chips or their menu, whichever shows: the other one is `hidden`. -->
      @if (library.hasFilters()) {
        <button
          mibButton
          type="button"
          mode="link"
          class="shrink-0 whitespace-nowrap no-underline"
          (click)="resetFilters()">
          Reset
        </button>
      }

      <span class="flex-1"></span>

      <!-- A round secondary button is the chip a sort menu opens from. -->
      <button
        mibButton
        type="button"
        theme="secondary"
        shape="round"
        class="shrink-0 whitespace-nowrap"
        [attr.aria-label]="'Sort the cases: ' + sortLabel()"
        [mibMenuTrigger]="sortMenu">
        {{ sortLabel() }}
        <svg mib-icon name="chevron-down" class="size-4"></svg>
      </button>
      <mib-menu
        #sortMenu
        align="end"
        [menuItems]="sortItems()"
        (itemSelected)="sortBy($event)"></mib-menu>

      <mib-button-toggles
        class="shrink-0"
        aria-label="How the cases are shown"
        [segments]="displaySegments"
        [value]="[display()]"
        (valueChange)="setDisplay($event)"></mib-button-toggles>
    </div>
  }
</ng-template>

<!-- Stacked, a phone opens the filters in a dialog instead. -->
<ng-template #filtersAside>
  <!-- Slides out beside the cases, so a choice shows as it is made. -->
  <aside
    id="library-filters"
    aria-label="Filters"
    [class]="filtersAsideClasses()"
    [attr.inert]="filtersOpen() ? null : ''">
    <div class="flex h-full flex-col overflow-hidden bg-white" [class]="filtersCardClasses()">
      <!-- A half of the row on a phone: headed like the cases beside it, at the same height. -->
      @if (notExpanded()) {
        <!-- The phone dialog's head: its count, its reset and its cross, on the cases' heading line. -->
        <div class="flex shrink-0 items-center gap-2 px-4 pt-4 pb-3" [class]="phoneHeaderClasses">
          <h2
            class="text-mib-branding-900 min-w-0 truncate text-2xl font-semibold not-expanded:leading-7.5">
            Filters
          </h2>
          @if (library.hasFilters()) {
            <span class="text-mib-branding-900 shrink-0 text-sm font-semibold">
              ({{ library.filterCount() }})
            </span>
          }
          <div class="ms-auto flex shrink-0 items-center gap-2">
            @if (library.hasFilters()) {
              <button
                mibButton
                type="button"
                mode="link"
                class="no-underline"
                (click)="resetFilters()">
                Reset
              </button>
            }
            <button
              mibButton
              type="button"
              theme="secondary"
              size="sm"
              aria-label="Close the filters"
              (click)="closeFilters()">
              <svg mib-icon name="x"></svg>
            </button>
          </div>
        </div>
      }
      <div class="mib-scrollbar min-h-0 flex-1 overflow-y-auto p-4 not-expanded:pt-0">
        @defer (when filtersOpen()) {
          <mai-case-filter-panel [sortable]="notExpanded()"></mai-case-filter-panel>
        }
      </div>
    </div>
  </aside>
</ng-template>

<!--
 One layout at a time, so the page holds a single chat: a second instance would race the first
 one over the shared message stream.
-->
@if (compact()) {
  <!-- One cell for both zones, so the one leaving and the one arriving cross-fade in place. -->
  <!-- No gutter: both zones go edge to edge on a phone. -->
  <main class="grid min-h-0 flex-1 grid-cols-1 grid-rows-1 overflow-hidden">
    <mai-search-tab
      class="col-start-1 row-start-1 min-h-0 transition-opacity duration-300 ease-in-out"
      [class.opacity-0]="mobileZone() !== LibraryZone.Chat"
      [class.pointer-events-none]="mobileZone() !== LibraryZone.Chat"
      [attr.inert]="mobileZone() === LibraryZone.Chat ? null : ''"></mai-search-tab>

    <div
      class="col-start-1 row-start-1 flex min-h-0 flex-col transition-opacity duration-300 ease-in-out"
      [class.opacity-0]="mobileZone() !== LibraryZone.Library"
      [class.pointer-events-none]="mobileZone() !== LibraryZone.Library"
      [attr.inert]="mobileZone() === LibraryZone.Library ? null : ''">
      <!-- The library is one surface, and it holds its own scroll: the card's edge stays put. -->
      <section [class]="librarySurfaceClasses">
        <div
          class="@container flex shrink-0 flex-col gap-3 px-4 pt-4 pb-3"
          [class]="phoneHeaderClasses">
          <ng-container
            [ngTemplateOutlet]="toolbar"
            [ngTemplateOutletContext]="{ $implicit: mobileCases.videos().length }"></ng-container>
        </div>

        <div class="flex min-h-0 flex-1 flex-col px-4 pb-4">
          <div
            class="mib-scrollbar min-h-0 flex-1 overflow-y-auto [overflow-anchor:none]"
            maiScrollPosition="case-library-mobile">
            <mai-case-list
              #mobileCases
              [categories]="categories()"
              [display]="shownDisplay()"></mai-case-list>
          </div>
        </div>
      </section>
    </div>
  </main>
} @else {
  <!-- The zone boxes share the row's content box: `100cqw` is the row inside its padding. -->
  <main
    #zonesRow
    class="@container flex min-h-0 flex-1 gap-4 p-2.5 not-expanded:overflow-hidden not-expanded:p-0 not-expanded:gap-0">
    <!-- A collapsed column keeps its content mounted, so it has to be taken out of the tab order. -->
    <div data-zone="chat" [class]="zoneWrapperClasses" [style]="zoneStyles()?.chat?.wrapper">
      <mai-search-tab
        #chatZone
        data-zone-content
        [class]="searchColumnClasses()"
        [style]="zoneStyles()?.chat?.inner"
        [attr.inert]="chatOnRow() ? null : ''"></mai-search-tab>
    </div>

    @if (notExpanded()) {
      <ng-container [ngTemplateOutlet]="filtersAside"></ng-container>
    }

    <div data-zone="library" [class]="zoneWrapperClasses" [style]="zoneStyles()?.library?.wrapper">
      <div
        data-zone-content
        [class]="listColumnClasses()"
        [style]="zoneStyles()?.library?.inner"
        [attr.inert]="libraryVisible() ? null : ''">
        <!--
         The library is one surface and it holds its own scroll, so its edge and its heading stay put
         while the cases move under them.
        -->
        <section [class]="librarySurfaceClasses">
          <div
            class="@container flex shrink-0 flex-col gap-3 px-6 pt-4 pb-3 not-expanded:px-4"
            [class]="phoneHeaderClasses">
            <ng-container
              [ngTemplateOutlet]="toolbar"
              [ngTemplateOutletContext]="{ $implicit: cases.videos().length }"></ng-container>
          </div>

          <div class="flex min-h-0 flex-1 px-6 pb-6 not-expanded:px-4 not-expanded:pb-4">
            @if (!notExpanded()) {
              <ng-container [ngTemplateOutlet]="filtersAside"></ng-container>
            }
            <div
              class="mib-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto [overflow-anchor:none] not-expanded:w-[calc(100cqw-2rem)] not-expanded:flex-none"
              maiScrollPosition="case-library-desktop">
              <!-- Pushed, the cases take their final width at once, the box clipping them as it moves. -->
              <mai-case-list
                #cases
                [style.width.px]="casesWidth()"
                [categories]="categories()"
                [display]="shownDisplay()"
                [filtersOpen]="filtersOpen() && !notExpanded()"
                [besideChat]="casesBesideZone()"></mai-case-list>
            </div>
          </div>
        </section>
      </div>
    </div>
  </main>
}
